import { useState, useEffect, useCallback } from "react";
import { useAtomValue } from "jotai";
import { useTasks, useTaskActions } from "../hooks/index.js";
import { selectedTaskAtom } from "../store/atoms/index.js";
import { baseBackendURL } from "../helpers/baseUrl";
import { makeAuthenticatedRequest } from "../helpers/authUtils";
import "../styles/RecurringModal.css";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DAY_MAP = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function findNthWeekday(year, month, week, dayOfWeek) {
  if (week === "last") {
    const d = new Date(year, month + 1, 0);
    while (d.getDay() !== dayOfWeek) d.setDate(d.getDate() - 1);
    return d;
  }
  const d = new Date(year, month, 1);
  let count = 0;
  while (d.getMonth() === month) {
    if (d.getDay() === dayOfWeek) {
      count++;
      if (count === week) return new Date(d);
    }
    d.setDate(d.getDate() + 1);
  }
  const last = new Date(year, month + 1, 0);
  while (last.getDay() !== dayOfWeek) last.setDate(last.getDate() - 1);
  return last;
}

function calculateNextRunDate(type, config) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (type === "daily") {
    const next = new Date(today);
    next.setDate(next.getDate() + config.interval);
    return next;
  }

  if (type === "weekly") {
    if (!config.days || config.days.length === 0) return null;
    const targetDays = config.days
      .map((d) => DAY_MAP[d])
      .sort((a, b) => a - b);
    const todayDay = today.getDay();
    for (let i = 1; i <= 7; i++) {
      if (targetDays.includes((todayDay + i) % 7)) {
        const next = new Date(today);
        next.setDate(today.getDate() + i);
        return next;
      }
    }
    return null;
  }

  if (type === "monthly_date") {
    const thisMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      config.dayOfMonth
    );
    if (thisMonth > today) return thisMonth;
    return new Date(today.getFullYear(), today.getMonth() + 1, config.dayOfMonth);
  }

  if (type === "monthly_dow") {
    const targetDay = DAY_MAP[config.day];
    const thisMonthCandidate = findNthWeekday(
      today.getFullYear(),
      today.getMonth(),
      config.week,
      targetDay
    );
    if (thisMonthCandidate > today) return thisMonthCandidate;
    const nextMonth = today.getMonth() + 1;
    return findNthWeekday(
      today.getFullYear() + Math.floor(nextMonth / 12),
      nextMonth % 12,
      config.week,
      targetDay
    );
  }

  return null;
}

function formatDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

const RecurringModal = ({ onClose }) => {
  const selectedTask = useAtomValue(selectedTaskAtom);
  const { saveRecurringTask, deleteRecurringTask } = useTaskActions();

  const [loading, setLoading] = useState(true);
  const [existingRuleId, setExistingRuleId] = useState(null);
  const [unit, setUnit] = useState("week");
  const [interval, setInterval] = useState(1);
  const [selectedDays, setSelectedDays] = useState(["monday"]);
  const [monthlyMode, setMonthlyMode] = useState("date");
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [weekOfMonth, setWeekOfMonth] = useState(1);
  const [dowDay, setDowDay] = useState("monday");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedTask) return;
    makeAuthenticatedRequest(
      `${baseBackendURL}/recurringTask?taskId=${selectedTask.task_id}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.rule) {
          const rule = data.rule;
          const config = JSON.parse(rule.recurrence_config);
          setExistingRuleId(rule.id);
          if (rule.recurrence_type === "daily") {
            setUnit("day");
            setInterval(config.interval);
          } else if (rule.recurrence_type === "weekly") {
            setUnit("week");
            setInterval(config.interval);
            setSelectedDays(config.days);
          } else if (rule.recurrence_type === "monthly_date") {
            setUnit("month");
            setMonthlyMode("date");
            setInterval(config.interval);
            setDayOfMonth(config.dayOfMonth);
          } else if (rule.recurrence_type === "monthly_dow") {
            setUnit("month");
            setMonthlyMode("dow");
            setInterval(config.interval);
            setWeekOfMonth(config.week);
            setDowDay(config.day);
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedTask?.task_id]);

  const getRecurrenceType = () => {
    if (unit === "day") return "daily";
    if (unit === "week") return "weekly";
    return monthlyMode === "date" ? "monthly_date" : "monthly_dow";
  };

  const getConfig = () => {
    if (unit === "day") return { interval };
    if (unit === "week") return { interval, days: selectedDays };
    if (monthlyMode === "date") return { interval, dayOfMonth };
    return { interval, week: weekOfMonth, day: dowDay };
  };

  const nextOccurrence = (() => {
    try {
      return calculateNextRunDate(getRecurrenceType(), getConfig());
    } catch {
      return null;
    }
  })();

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day)
        ? prev.length > 1
          ? prev.filter((d) => d !== day)
          : prev
        : [...prev, day]
    );
  };

  const handleSave = async () => {
    if (unit === "week" && selectedDays.length === 0) return;
    setSaving(true);
    await saveRecurringTask({
      taskId: selectedTask.task_id,
      type: getRecurrenceType(),
      config: getConfig(),
    });
    setSaving(false);
    onClose();
  };

  const handleRemove = async () => {
    setSaving(true);
    await deleteRecurringTask(selectedTask.task_id);
    setSaving(false);
    onClose();
  };

  if (loading) {
    return <div className="recurring-modal"><p>Loading...</p></div>;
  }

  return (
    <div className="recurring-modal">
      <h2>{existingRuleId ? "Edit Recurring Schedule" : "Set Recurring Schedule"}</h2>
      <p className="recurring-task-name">{selectedTask?.task_name}</p>

      <div className="recurring-row">
        <label>Repeat every</label>
        <div className="recurring-interval">
          <input
            type="number"
            min={1}
            max={unit === "day" ? 365 : unit === "week" ? 52 : 12}
            value={interval}
            onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
          />
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          >
            <option value="day">day(s)</option>
            <option value="week">week(s)</option>
            <option value="month">month(s)</option>
          </select>
        </div>
      </div>

      {unit === "week" && (
        <div className="recurring-section">
          <label>On</label>
          <div className="day-pills">
            {DAYS.map((day, i) => (
              <button
                key={day}
                type="button"
                className={`day-pill ${selectedDays.includes(DAY_KEYS[i]) ? "active" : ""}`}
                onClick={() => toggleDay(DAY_KEYS[i])}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}

      {unit === "month" && (
        <div className="recurring-section">
          <label>On</label>
          <div className="monthly-options">
            <label className="monthly-radio">
              <input
                type="radio"
                name="monthlyMode"
                value="date"
                checked={monthlyMode === "date"}
                onChange={() => setMonthlyMode("date")}
              />
              <span>Day</span>
              <input
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                disabled={monthlyMode !== "date"}
                onChange={(e) =>
                  setDayOfMonth(Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))
                }
                className="day-of-month-input"
              />
              <span>of the month</span>
            </label>

            <label className="monthly-radio">
              <input
                type="radio"
                name="monthlyMode"
                value="dow"
                checked={monthlyMode === "dow"}
                onChange={() => setMonthlyMode("dow")}
              />
              <span>The</span>
              <select
                value={weekOfMonth}
                disabled={monthlyMode !== "dow"}
                onChange={(e) =>
                  setWeekOfMonth(
                    e.target.value === "last" ? "last" : parseInt(e.target.value)
                  )
                }
              >
                <option value={1}>1st</option>
                <option value={2}>2nd</option>
                <option value={3}>3rd</option>
                <option value={4}>4th</option>
                <option value="last">Last</option>
              </select>
              <select
                value={dowDay}
                disabled={monthlyMode !== "dow"}
                onChange={(e) => setDowDay(e.target.value)}
              >
                {DAY_KEYS.map((d, i) => (
                  <option key={d} value={d}>{DAYS[i]}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}

      {nextOccurrence && (
        <p className="recurring-next">
          Next: <strong>{formatDate(nextOccurrence)}</strong>
        </p>
      )}

      <div className="recurring-actions">
        {existingRuleId && (
          <button
            type="button"
            className="recurring-remove-btn"
            onClick={handleRemove}
            disabled={saving}
          >
            Remove recurrence
          </button>
        )}
        <button
          type="button"
          className="recurring-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default RecurringModal;
